import React from 'react';
import { Box } from '@mui/material';

// Shared Components
import SolutionHero from '../components/SolutionHero';
import ServicesGrid from '../components/ServicesGrid';
import ProcessTimeline from '../components/ProcessTimeline';
import BenefitsSection from '../components/BenefitsSection';
import FAQAccordion from '../components/FAQAccordion';
import SolutionCTA from '../components/SolutionCTA';

// Icons
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import VerifiedIcon from '@mui/icons-material/Verified';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ShieldIcon from '@mui/icons-material/Shield';

const GenAIEngineering = () => {
    // Page Content
    const services = [
        {
            icon: <ModelTrainingIcon sx={{ fontSize: 28 }} />,
            title: "Custom LLM Fine-Tuning",
            description: "Train models on your proprietary data to understand your domain, terminology, and business context with high accuracy."
        },
        {
            icon: <StorageIcon sx={{ fontSize: 28 }} />,
            title: "RAG Implementation",
            description: "Build Retrieval-Augmented Generation pipelines that ground LLM responses in your knowledge base for accurate, up-to-date answers."
        },
        {
            icon: <TuneIcon sx={{ fontSize: 28 }} />,
            title: "Prompt Engineering",
            description: "Design, test, and optimize prompts for consistent, reliable outputs across your use cases and edge cases."
        },
        {
            icon: <VerifiedIcon sx={{ fontSize: 28 }} />,
            title: "Model Evaluation & Selection",
            description: "Benchmark OpenAI, Anthropic, Llama, Mistral, and other models against your specific requirements and budget."
        },
        {
            icon: <RocketLaunchIcon sx={{ fontSize: 28 }} />,
            title: "LLM Deployment & Scaling",
            description: "Deploy models to production with auto-scaling, load balancing, and cost-optimized inference infrastructure."
        },
        {
            icon: <ShieldIcon sx={{ fontSize: 28 }} />,
            title: "Guardrails & Safety",
            description: "Implement content filters, output validation, PII detection, and safety measures for responsible AI deployment."
        }
    ];

    const processSteps = [
        {
            title: "Requirement Analysis",
            description: "Define use cases, success metrics, data availability, and integration requirements."
        },
        {
            title: "Data Preparation",
            description: "Clean, curate, and structure training data. Build evaluation datasets and benchmarks."
        },
        {
            title: "Model Training",
            description: "Fine-tune base models, implement RAG pipelines, and iterate based on evaluation results."
        },
        {
            title: "Production Deployment",
            description: "Deploy with monitoring, safety checks, and A/B testing frameworks for continuous improvement."
        }
    ];

    const benefits = [
        {
            title: "Domain-Specific Accuracy",
            description: "Generic models miss nuances. Our fine-tuned models understand your industry jargon, processes, and contextual requirements."
        },
        {
            title: "Reduced Hallucinations",
            description: "RAG architectures ground responses in verified data sources, dramatically reducing false or fabricated outputs."
        },
        {
            title: "Cost Optimization",
            description: "Right-size your model choice: use smaller, faster models where possible and premium models only where needed."
        },
        {
            title: "Enterprise-Grade Security",
            description: "On-premise deployment options, data encryption, access controls, and audit trails for compliance requirements."
        }
    ];

    const faqs = [
        {
            question: "When should I fine-tune vs. use prompting?",
            answer: "Fine-tuning is ideal when you need consistent format, domain-specific knowledge, or reduced prompt tokens. Prompting works well for flexible, general-purpose tasks. We help you choose the right approach for each use case."
        },
        {
            question: "What's the difference between fine-tuning and RAG?",
            answer: "Fine-tuning bakes knowledge into model weights (good for style, format, domain understanding). RAG retrieves current data at query time (good for frequently changing information, citations, and accuracy). Often, the best solution uses both."
        },
        {
            question: "What models do you work with?",
            answer: "We work with OpenAI (GPT-4, GPT-4o), Anthropic (Claude), Meta (Llama 3), Mistral, Cohere, and open-source models. We select based on your performance requirements, budget, and deployment constraints."
        },
        {
            question: "How do you handle sensitive data?",
            answer: "We offer on-premise deployment, private cloud options, PII anonymization in training data, and strict data governance. Your data never leaves your controlled environment if required."
        },
        {
            question: "What infrastructure do you deploy on?",
            answer: "We deploy on AWS (SageMaker, Bedrock), GCP (Vertex AI), Azure (OpenAI Service), and on-premise GPU clusters. We optimize for your existing cloud relationships and constraints."
        }
    ];

    const ctaStats = [
        { value: "85%", label: "Accuracy Improvement" },
        { value: "70%", label: "Reduced Hallucinations" },
        { value: "40%", label: "Cost Savings" },
        { value: "3x", label: "Faster Development" }
    ];

    return (
        <Box sx={{ bgcolor: '#030014', minHeight: '100vh' }}>
            {/* Hero Section */}
            <SolutionHero
                headline="Custom LLMs."
                highlightedText="Real Business Impact."
                subheadline="Fine-tuned models, RAG pipelines, and production-grade AI that understands your domain. Stop fighting generic models—build AI that speaks your language."
                primaryCTA="Explore Solutions"
                primaryCTALink="/contact-us"
                secondaryCTA="Talk to an Engineer"
                secondaryCTALink="#process"
            />

            {/* Services Grid */}
            <ServicesGrid
                title="GenAI Engineering Services"
                subtitle="LLM Capabilities"
                services={services}
            />

            {/* Process Timeline */}
            <ProcessTimeline
                title="From Prototype to Production"
                subtitle="Our LLM Development Process"
                steps={processSteps}
            />

            {/* Benefits Section */}
            <BenefitsSection
                title="Why Custom GenAI Wins"
                subtitle="The Custom Model Advantage"
                benefits={benefits}
            />

            {/* FAQ Section */}
            <FAQAccordion
                title="Frequently Asked Questions"
                subtitle="GenAI Questions"
                faqs={faqs}
            />

            {/* CTA Section */}
            <SolutionCTA
                headline="Ready for AI That Truly Understands You?"
                subheadline="Generic models get you 80% there. Custom GenAI gets you to production with precision, speed, and reliability."
                primaryCTA="Start a Conversation"
                primaryCTALink="/contact-us"
                secondaryCTA="See Case Studies"
                secondaryCTALink="/case-studies"
                stats={ctaStats}
            />
        </Box>
    );
};

export default GenAIEngineering;
